/**
 * VideoCaptureFeedback Component
 *
 * Displays real-time feedback during video capture including:
 * - Recording indicator (pulsing red dot)
 * - Timer/countdown display
 * - Motion/settled status
 * - Frame count
 */

import { cn } from '@/lib/utils';

const SERVICE_TAG = '[VIDEO_FEEDBACK]';

/**
 * Props for VideoCaptureFeedback component
 */
export interface VideoCaptureFeedbackProps {
  /** Whether recording is active */
  isRecording: boolean;
  /** Current recording duration in milliseconds */
  currentDurationMs: number;
  /** Target recording duration in milliseconds */
  targetDurationMs: number;
  /** Number of frames captured so far */
  frameCount: number;
  /** Whether dice are currently settled (optional) */
  isSettled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Format milliseconds to MM:SS.ms display
 */
function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((ms % 1000) / 100);

  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  }
  return `${seconds}.${tenths}s`;
}

/**
 * Recording indicator with pulsing animation
 */
function RecordingIndicator({ isRecording }: { isRecording: boolean }) {
  if (!isRecording) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'w-3 h-3 rounded-full bg-red-500',
          'animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.6)]'
        )}
        aria-label="Recording"
      />
      <span className="text-sm font-medium text-red-500">REC</span>
    </div>
  );
}

/**
 * Settled status badge
 */
function SettledBadge({ isSettled }: { isSettled: boolean }) {
  return (
    <div
      className={cn(
        'px-2 py-0.5 rounded-full text-xs font-medium transition-colors',
        isSettled
          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
          : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
      )}
    >
      {isSettled ? 'Settled' : 'Moving'}
    </div>
  );
}

/**
 * VideoCaptureFeedback displays real-time information during video capture
 */
export function VideoCaptureFeedback({
  isRecording,
  currentDurationMs,
  targetDurationMs,
  frameCount,
  isSettled = false,
  className
}: VideoCaptureFeedbackProps) {
  if (!isRecording) {
    return null;
  }

  const progress = Math.min((currentDurationMs / targetDurationMs) * 100, 100);
  const remainingMs = Math.max(targetDurationMs - currentDurationMs, 0);

  console.info(`${SERVICE_TAG} Rendering feedback`, {
    progress: progress.toFixed(1),
    frameCount,
    isSettled
  });

  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-3 rounded-lg',
        'bg-secondary/80 backdrop-blur-sm border border-border/50',
        className
      )}
    >
      {/* Top row: Recording indicator and timer */}
      <div className="flex items-center justify-between">
        <RecordingIndicator isRecording={isRecording} />
        <div className="text-sm text-muted-foreground">
          {formatDuration(currentDurationMs)} / {formatDuration(targetDurationMs)}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-100',
            'bg-gradient-to-r from-red-500 to-red-400'
          )}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Bottom row: Status info */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Frames:</span>
          <span className="font-mono text-foreground">{frameCount}</span>
        </div>
        <SettledBadge isSettled={isSettled} />
      </div>

      {/* Remaining time callout when close to end */}
      {remainingMs > 0 && remainingMs <= 1000 && (
        <div className="text-center text-xs text-amber-400 animate-pulse">
          Finishing in {Math.ceil(remainingMs / 100) / 10}s...
        </div>
      )}
    </div>
  );
}

export default VideoCaptureFeedback;
